import * as React from "react";
import { CaretDownIcon, CheckIcon, DotsThreeIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";

import { Badge } from "@libs/zapi-ui/ds/components/badge";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/ds/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle
} from "@libs/zapi-ui/ds/components/field";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/ds/components/popover";
import { RadioGroup, RadioGroupItem } from "@libs/zapi-ui/ds/components/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import { Separator } from "@libs/zapi-ui/ds/components/separator";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";

type ProjectPerson = { name: string; initials: string; avatarUrl?: string };

type ProjectVersion = { id: string; name: string; updated: string; author?: ProjectPerson; draft?: boolean };

type ProjectVersionScope = "frame" | "full";

type NewVersionRequest = { name: string; baseId: string; scope: ProjectVersionScope };

type ProjectVersionLabels = {
  versions: string;
  latest: string;
  draft: string;
  trigger: (version: string) => string;
  rowActions: (version: string) => string;
  rename: string;
  remove: string;
  removing: (version: string) => string;
  keepOneVersion: string;
  newVersion: string;
  createVersion: string;
  creating: string;
  firstVersionDescription: string;
  newVersionDescription: string;
  name: string;
  nameTaken: string;
  basedOn: string;
  draftNoAnalysis: string;
  copy: string;
  frameOnly: string;
  frameOnlyDescription: string;
  fullAnalysis: string;
  fullAnalysisDescription: string;
  noAnalysisYet: string;
  renameTitle: string;
  renameDescription: string;
  save: string;
  saving: string;
  cancel: string;
  close: string;
  loading: string;
};

type ProjectVersionActions = {
  versioned?: boolean;
  editable?: boolean;
  newVersionName?: string;
  onCreate?: (request: NewVersionRequest) => unknown;
  onRename?: (version: ProjectVersion, name: string) => unknown;
  onRemove?: (version: ProjectVersion) => unknown;
  defaultOpen?: boolean;
};

// Project.name and Project.versionDisplayName are VARCHAR(191), shorter than the API's 255.
const NAME_MAX_LENGTH = 191;

function nextVersionName(names: string[]) {
  let highest = 0;
  for (const name of names) {
    if (name === "New Version") highest = Math.max(highest, 1);
    const numbered = /^New Version (\d+)$/.exec(name);
    if (numbered) highest = Math.max(highest, Number(numbered[1]));
  }
  return highest === 0 ? "New Version" : `New Version ${highest + 1}`;
}

function versionMeta(version: ProjectVersion) {
  return [version.author?.name, version.updated].filter(Boolean).join(" · ");
}

function isTaken(name: string, others: ProjectVersion[]) {
  const needle = name.trim().toLowerCase();
  return others.some((version) => version.name.trim().toLowerCase() === needle);
}

async function settle(result: unknown, setPending: (pending: boolean) => void, close: () => void) {
  if (!(result instanceof Promise)) {
    close();
    return;
  }
  setPending(true);
  let kept = false;
  try {
    kept = (await result) === false;
  } catch {
    kept = true;
  } finally {
    setPending(false);
  }
  if (!kept) close();
}

function VersionBadges({
  version,
  latest,
  labels
}: {
  version: ProjectVersion;
  latest: boolean;
  labels: ProjectVersionLabels;
}) {
  return (
    <>
      {latest && (
        <Badge variant="secondary" className="h-5 shrink-0 rounded-full">
          {labels.latest}
        </Badge>
      )}
      {version.draft && (
        <Badge variant="outline" className="h-5 shrink-0 rounded-full">
          {labels.draft}
        </Badge>
      )}
    </>
  );
}

function VersionRowActions({
  version,
  canRemove,
  removing,
  labels,
  onRename,
  onRemove
}: {
  version: ProjectVersion;
  canRemove: boolean;
  removing: boolean;
  labels: ProjectVersionLabels;
  onRename?: () => void;
  onRemove?: () => void;
}) {
  if (removing) {
    return (
      <span className="flex size-8 shrink-0 items-center justify-center text-muted-foreground">
        <Spinner aria-label={labels.removing(version.name)} />
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            shape="pill"
            aria-label={labels.rowActions(version.name)}
            className="text-muted-foreground transition-opacity pointer-fine:opacity-0 pointer-fine:group-hover/version:opacity-100 pointer-fine:group-focus-within/version:opacity-100 data-[popup-open]:bg-accent data-[popup-open]:opacity-100"
          />
        }
      >
        <DotsThreeIcon weight="bold" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4} className="w-52 rounded-xl p-1.5">
        {onRename && (
          <DropdownMenuItem onClick={onRename} className="rounded-lg">
            <PencilSimpleIcon />
            {labels.rename}
          </DropdownMenuItem>
        )}
        {onRemove && (
          <DropdownMenuItem variant="destructive" disabled={!canRemove} onClick={onRemove} className="rounded-lg">
            <TrashIcon />
            {labels.remove}
          </DropdownMenuItem>
        )}
        {onRemove && !canRemove && (
          <p className="typo-caption px-2 pt-1 pb-1.5 text-muted-foreground">{labels.keepOneVersion}</p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ProjectVersionList({
  versions,
  value,
  labels,
  onSelect,
  onNewVersion,
  onRename,
  onRemove,
  removingId,
  className,
  ...props
}: {
  versions: ProjectVersion[];
  value: string;
  labels: ProjectVersionLabels;
  onSelect?: (version: ProjectVersion) => void;
  onNewVersion?: () => void;
  onRename?: (version: ProjectVersion) => void;
  onRemove?: (version: ProjectVersion) => void;
  removingId?: string | null;
} & Omit<React.ComponentProps<"div">, "onSelect">) {
  const latestId = versions[0]?.id;
  const currentId = versions.some((version) => version.id === value) ? value : latestId;
  const hasActions = Boolean(onRename || onRemove);

  return (
    <div data-slot="project-version-list" className={cn("flex min-h-0 flex-col", className)} {...props}>
      <div className="flex items-center justify-between px-2.5 pt-2 pb-1.5">
        <span className="typo-overline text-muted-foreground">{labels.versions}</span>
        <span className="typo-caption text-muted-foreground tabular-nums">{versions.length}</span>
      </div>
      <ul aria-label={labels.versions} className="flex min-h-0 flex-col gap-0.5 overflow-y-auto">
        {versions.map((version) => {
          const current = version.id === currentId;
          const removing = version.id === removingId;
          return (
            <li
              key={version.id}
              data-removing={removing || undefined}
              className="group/version flex items-center gap-1 rounded-lg pe-1 transition-colors hover:bg-accent has-[[aria-current]]:bg-accent/60 data-removing:pointer-events-none data-removing:opacity-60"
            >
              <button
                type="button"
                aria-current={current || undefined}
                disabled={removing}
                onClick={() => onSelect?.(version)}
                className="flex min-w-0 flex-1 cursor-pointer items-start gap-2.5 rounded-lg py-2 ps-2.5 text-start outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                <span className="flex size-5 shrink-0 items-center justify-center">
                  {current && <CheckIcon aria-hidden="true" weight="bold" className="size-4 text-foreground" />}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="typo-label-md truncate text-foreground">{version.name}</span>
                    <VersionBadges version={version} latest={version.id === latestId} labels={labels} />
                  </span>
                  <span className="typo-caption truncate text-muted-foreground">{versionMeta(version)}</span>
                </span>
              </button>
              {hasActions && (
                <VersionRowActions
                  version={version}
                  canRemove={versions.length > 1}
                  removing={removing}
                  labels={labels}
                  onRename={onRename && (() => onRename(version))}
                  onRemove={onRemove && (() => onRemove(version))}
                />
              )}
            </li>
          );
        })}
      </ul>
      {onNewVersion && (
        <>
          <Separator className="my-1.5" />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="typo-label-md w-full justify-start gap-2.5 rounded-lg px-2.5"
            onClick={onNewVersion}
          >
            <PlusIcon data-icon="inline-start" weight="bold" />
            {labels.newVersion}
          </Button>
        </>
      )}
    </div>
  );
}

function NewVersionForm({
  versions,
  defaultBaseId,
  defaultName,
  pending = false,
  labels,
  onSubmit,
  onCancel,
  className
}: {
  versions: ProjectVersion[];
  defaultBaseId?: string;
  defaultName?: string;
  pending?: boolean;
  labels: ProjectVersionLabels;
  onSubmit: (request: NewVersionRequest) => void;
  onCancel: () => void;
  className?: string;
}) {
  const analyzed = versions.filter((version) => !version.draft);
  const [name, setName] = React.useState(() => defaultName ?? nextVersionName(versions.map((version) => version.name)));
  const [scope, setScope] = React.useState<ProjectVersionScope>("frame");
  const [baseId, setBaseId] = React.useState(
    () => versions.find((version) => version.id === defaultBaseId)?.id ?? versions[0]?.id ?? ""
  );
  const nameId = React.useId();
  const baseLabelId = React.useId();
  const trimmed = name.trim();
  const duplicate = trimmed !== "" && isTaken(trimmed, versions);
  const base = versions.find((version) => version.id === baseId);
  const invalidBase = !base || (scope === "full" && Boolean(base.draft));
  const scopeOptions: { value: ProjectVersionScope; title: string; description: string }[] = [
    { value: "frame", title: labels.frameOnly, description: labels.frameOnlyDescription },
    { value: "full", title: labels.fullAnalysis, description: labels.fullAnalysisDescription }
  ];

  const changeScope = (next: ProjectVersionScope) => {
    setScope(next);
    if (next === "full" && base?.draft && analyzed[0]) setBaseId(analyzed[0].id);
  };

  return (
    <form
      data-slot="new-version-form"
      className={cn("flex flex-col gap-4", className)}
      onSubmit={(event) => {
        event.preventDefault();
        if (pending || !trimmed || duplicate || invalidBase) return;
        onSubmit({ name: trimmed, baseId, scope });
      }}
    >
      <Field data-invalid={duplicate || undefined}>
        <FieldLabel htmlFor={nameId}>{labels.name}</FieldLabel>
        <Input
          id={nameId}
          value={name}
          maxLength={NAME_MAX_LENGTH}
          disabled={pending}
          aria-invalid={duplicate || undefined}
          onChange={(event) => setName(event.target.value)}
        />
        {duplicate && <span className="typo-caption text-destructive">{labels.nameTaken}</span>}
      </Field>
      <Field>
        <FieldLabel id={baseLabelId}>{labels.basedOn}</FieldLabel>
        <Select
          items={versions.map((version) => ({ value: version.id, label: version.name }))}
          value={baseId}
          disabled={pending}
          onValueChange={(next) => typeof next === "string" && setBaseId(next)}
        >
          <SelectTrigger aria-labelledby={baseLabelId} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {versions.map((version) => (
              <SelectItem
                key={version.id}
                value={version.id}
                disabled={scope === "full" && version.draft}
                className="py-2"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="typo-label-md truncate">{version.name}</span>
                  <span className="typo-caption truncate text-muted-foreground">
                    {[versionMeta(version), version.draft ? labels.draftNoAnalysis : ""].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <FieldSet className="gap-3">
        <FieldLegend variant="label" className="mb-0">
          {labels.copy}
        </FieldLegend>
        <RadioGroup
          value={scope}
          disabled={pending}
          onValueChange={(next) => changeScope(next as ProjectVersionScope)}
          className="gap-2"
        >
          {scopeOptions.map((option) => {
            const id = `${nameId}-${option.value}`;
            const unavailable = option.value === "full" && analyzed.length === 0;
            return (
              <FieldLabel key={option.value} htmlFor={id} className="rounded-xl">
                <Field orientation="horizontal" data-disabled={unavailable || undefined} className="rounded-xl">
                  <FieldContent>
                    <FieldTitle>{option.title}</FieldTitle>
                    <FieldDescription>{unavailable ? labels.noAnalysisYet : option.description}</FieldDescription>
                  </FieldContent>
                  <RadioGroupItem value={option.value} id={id} disabled={unavailable} />
                </Field>
              </FieldLabel>
            );
          })}
        </RadioGroup>
      </FieldSet>
      <DialogFooter className="gap-2">
        <Button type="button" variant="ghost" shape="pill" disabled={pending} onClick={onCancel}>
          {labels.cancel}
        </Button>
        <Button type="submit" shape="pill" disabled={pending || !trimmed || duplicate || invalidBase}>
          {pending && <Spinner data-icon="inline-start" aria-label={labels.loading} />}
          {pending ? labels.creating : labels.createVersion}
        </Button>
      </DialogFooter>
    </form>
  );
}

function NewVersionDialog({
  open,
  onOpenChange,
  versions,
  defaultBaseId,
  defaultName,
  pending: pendingProp = false,
  labels,
  onSubmit
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versions: ProjectVersion[];
  defaultBaseId?: string;
  defaultName?: string;
  pending?: boolean;
  labels: ProjectVersionLabels;
  onSubmit: (request: NewVersionRequest) => unknown;
}) {
  const [submitting, setSubmitting] = React.useState(false);
  const pending = pendingProp || submitting;
  const first = versions.length <= 1;

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent closeLabel={labels.close} className="gap-5 rounded-2xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="typo-h5">{labels.newVersion}</DialogTitle>
          <DialogDescription>{first ? labels.firstVersionDescription : labels.newVersionDescription}</DialogDescription>
        </DialogHeader>
        <NewVersionForm
          versions={versions}
          defaultBaseId={defaultBaseId}
          defaultName={defaultName}
          pending={pending}
          labels={labels}
          onCancel={() => onOpenChange(false)}
          onSubmit={(request) => void settle(onSubmit(request), setSubmitting, () => onOpenChange(false))}
        />
      </DialogContent>
    </Dialog>
  );
}

function RenameVersionForm({
  version,
  versions,
  pending = false,
  labels,
  onSubmit,
  onCancel,
  className
}: {
  version: ProjectVersion;
  versions: ProjectVersion[];
  pending?: boolean;
  labels: ProjectVersionLabels;
  onSubmit: (name: string) => void;
  onCancel: () => void;
  className?: string;
}) {
  const [name, setName] = React.useState(version.name);
  const nameId = React.useId();
  const trimmed = name.trim();
  const duplicate =
    trimmed !== "" &&
    isTaken(
      trimmed,
      versions.filter((candidate) => candidate.id !== version.id)
    );
  const unchanged = trimmed === version.name.trim();

  return (
    <form
      data-slot="rename-version-form"
      className={cn("flex flex-col gap-4", className)}
      onSubmit={(event) => {
        event.preventDefault();
        if (pending || !trimmed || duplicate || unchanged) return;
        onSubmit(trimmed);
      }}
    >
      <Field data-invalid={duplicate || undefined}>
        <FieldLabel htmlFor={nameId}>{labels.name}</FieldLabel>
        <Input
          id={nameId}
          value={name}
          maxLength={NAME_MAX_LENGTH}
          disabled={pending}
          aria-invalid={duplicate || undefined}
          onChange={(event) => setName(event.target.value)}
        />
        {duplicate && <span className="typo-caption text-destructive">{labels.nameTaken}</span>}
      </Field>
      <DialogFooter className="gap-2">
        <Button type="button" variant="ghost" shape="pill" disabled={pending} onClick={onCancel}>
          {labels.cancel}
        </Button>
        <Button type="submit" shape="pill" disabled={pending || !trimmed || duplicate || unchanged}>
          {pending && <Spinner data-icon="inline-start" aria-label={labels.loading} />}
          {pending ? labels.saving : labels.save}
        </Button>
      </DialogFooter>
    </form>
  );
}

function RenameVersionDialog({
  open,
  onOpenChange,
  version,
  versions,
  pending: pendingProp = false,
  labels,
  onSubmit
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: ProjectVersion | null;
  versions: ProjectVersion[];
  pending?: boolean;
  labels: ProjectVersionLabels;
  onSubmit: (version: ProjectVersion, name: string) => unknown;
}) {
  const [submitting, setSubmitting] = React.useState(false);
  const pending = pendingProp || submitting;

  return (
    <Dialog open={open && version !== null} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent closeLabel={labels.close} className="gap-5 rounded-2xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="typo-h5">{labels.renameTitle}</DialogTitle>
          <DialogDescription>{labels.renameDescription}</DialogDescription>
        </DialogHeader>
        {version && (
          <RenameVersionForm
            key={version.id}
            version={version}
            versions={versions}
            pending={pending}
            labels={labels}
            onCancel={() => onOpenChange(false)}
            onSubmit={(name) => void settle(onSubmit(version, name), setSubmitting, () => onOpenChange(false))}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

const versionTriggerAppearance = {
  glass:
    "typo-label-sm h-7 border-white/40 bg-background/70 px-2.5 text-foreground shadow-xs backdrop-blur-md hover:bg-background/90 data-[popup-open]:bg-background dark:border-white/10",
  plain:
    "typo-label-md h-8 border-transparent bg-transparent px-2.5 text-muted-foreground shadow-none hover:bg-accent hover:text-foreground data-[popup-open]:bg-accent data-[popup-open]:text-foreground dark:bg-transparent dark:hover:bg-accent"
};

const versionLabelAppearance = {
  glass:
    "typo-label-sm h-7 border border-white/40 bg-background/70 px-2.5 text-foreground shadow-xs backdrop-blur-md dark:border-white/10",
  plain: "typo-label-md h-8 px-2.5 text-muted-foreground"
};

function ProjectVersionMenu({
  versions,
  value,
  onValueChange,
  appearance = "glass",
  versioned = true,
  editable = true,
  newVersionName,
  labels,
  onCreate,
  onRename,
  onRemove,
  defaultOpen = false,
  className,
  ...props
}: ProjectVersionActions & {
  versions: ProjectVersion[];
  value: string;
  onValueChange?: (version: ProjectVersion) => void;
  appearance?: keyof typeof versionTriggerAppearance;
  labels: ProjectVersionLabels;
} & React.ComponentProps<"div">) {
  const [open, setOpen] = React.useState(defaultOpen);
  const [dialog, setDialog] = React.useState<"new" | "rename" | null>(null);
  const [renameTarget, setRenameTarget] = React.useState<ProjectVersion | null>(null);
  const [removingId, setRemovingId] = React.useState<string | null>(null);
  const current = versions.find((version) => version.id === value) ?? versions[0];
  const canCreate = editable && Boolean(onCreate);

  const openDialog = (next: "new" | "rename") => {
    setOpen(false);
    setDialog(next);
  };

  const remove = async (version: ProjectVersion) => {
    const result = onRemove?.(version);
    if (!(result instanceof Promise)) return;
    setRemovingId(version.id);
    try {
      await result;
    } catch {
      // The screen reports the failure; the row stays.
    } finally {
      setRemovingId(null);
    }
  };

  let trigger: React.ReactNode;
  if (!versioned && canCreate) {
    trigger = (
      <Button
        variant="outline"
        size={appearance === "plain" ? "sm" : "xs"}
        shape="pill"
        className={cn("max-w-full min-w-0 shrink gap-1", versionTriggerAppearance[appearance])}
        onClick={() => openDialog("new")}
      >
        <PlusIcon data-icon="inline-start" weight="bold" className="size-3" />
        <span className="truncate">{labels.createVersion}</span>
      </Button>
    );
  } else if (!versioned) {
    trigger = (
      <span
        title={current?.name}
        className={cn("inline-flex max-w-full min-w-0 items-center rounded-full", versionLabelAppearance[appearance])}
      >
        <span className="truncate">{current?.name}</span>
      </span>
    );
  } else {
    trigger = (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              variant="outline"
              size={appearance === "plain" ? "sm" : "xs"}
              shape="pill"
              aria-label={labels.trigger(current?.name ?? "")}
              className={cn("max-w-full min-w-0 shrink gap-1", versionTriggerAppearance[appearance])}
            />
          }
        >
          <span className="truncate">{current?.name}</span>
          <CaretDownIcon data-icon="inline-end" weight="bold" className="size-3 shrink-0 text-muted-foreground" />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={8}
          className="max-h-[min(28rem,var(--available-height))] w-80 max-w-[calc(100vw-2rem)] gap-0 rounded-xl p-1.5"
        >
          <ProjectVersionList
            versions={versions}
            value={current?.id ?? ""}
            labels={labels}
            removingId={removingId}
            onSelect={(version) => {
              setOpen(false);
              if (version.id !== current?.id) onValueChange?.(version);
            }}
            onNewVersion={canCreate ? () => openDialog("new") : undefined}
            onRename={
              editable && onRename
                ? (version) => {
                    setRenameTarget(version);
                    openDialog("rename");
                  }
                : undefined
            }
            onRemove={editable && onRemove ? (version) => void remove(version) : undefined}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div data-slot="project-version-menu" className={cn("flex max-w-full min-w-0", className)} {...props}>
      {trigger}
      {canCreate && onCreate && (
        <NewVersionDialog
          open={dialog === "new"}
          onOpenChange={(next) => !next && setDialog(null)}
          versions={versions}
          defaultBaseId={current?.id}
          defaultName={newVersionName}
          labels={labels}
          onSubmit={onCreate}
        />
      )}
      {editable && onRename && (
        <RenameVersionDialog
          open={dialog === "rename"}
          onOpenChange={(next) => !next && setDialog(null)}
          version={renameTarget}
          versions={versions}
          labels={labels}
          onSubmit={onRename}
        />
      )}
    </div>
  );
}

export {
  NewVersionDialog,
  NewVersionForm,
  nextVersionName,
  ProjectVersionList,
  ProjectVersionMenu,
  RenameVersionDialog,
  RenameVersionForm,
  type NewVersionRequest,
  type ProjectVersion,
  type ProjectVersionActions,
  type ProjectVersionLabels,
  type ProjectVersionScope
};
